import { z } from 'zod';
import { SEARCH_KINDS, type SearchKind } from './domain.js';

/**
 * Global search contract.
 *
 * The whole point is that one keystroke returns mixed-type results, each already carrying
 * what its card needs to render. One box searches names, addresses, phone numbers, emails,
 * document numbers and locations at once, so nobody has to choose a screen before they can
 * look something up.
 */

export const searchRequestSchema = z.object({
  /** What the user typed. Matched against names, addresses, phones, emails and numbers. */
  q: z.string().min(1).max(120),
  /**
   * Restrict to certain card types. Omitted means everything.
   *
   * Wrapped so one value is as valid as several. A query string carries repetition, not arrays:
   * `?kinds=customer&kinds=job` reaches the server as an array and `?kinds=customer` as a bare
   * string, so a caller narrowing to a single type — which is the common case for a picker —
   * was refused with a 400 for asking correctly.
   */
  kinds: z
    .preprocess((value) => (typeof value === 'string' ? [value] : value), z.array(z.enum(SEARCH_KINDS)))
    .optional(),
  limit: z.coerce.number().int().min(1).max(50).default(12),
  /** Group into per-type buckets (for the autocomplete dropdown) or return one flat ranked list. */
  grouped: z.coerce.boolean().default(true),
});
export type SearchRequest = z.infer<typeof searchRequestSchema>;

/** One card in the results. `payload` is type-narrowed per kind by the consumers below. */
export const searchHitSchema = z.object({
  kind: z.enum(SEARCH_KINDS),
  id: z.string().uuid(),
  title: z.string(),
  subtitle: z.string().nullable(),
  /** 0..1, already blended from text relevance and recency. */
  score: z.number(),
  /** Which field produced the match, so the UI can show why this result appeared. */
  matchedOn: z.enum(['name', 'address', 'phone', 'email', 'number', 'text']).nullable(),
  payload: z.record(z.unknown()),
  occurredAt: z.string().datetime().nullable(),
});
export type SearchHit = z.infer<typeof searchHitSchema>;

export const searchResponseSchema = z.object({
  query: z.string(),
  /** Flat, ranked across all types. Always present. */
  hits: z.array(searchHitSchema),
  /** Present when `grouped` was requested: the same hits bucketed by kind, order preserved. */
  groups: z
    .array(z.object({ kind: z.enum(SEARCH_KINDS), total: z.number(), hits: z.array(searchHitSchema) }))
    .optional(),
  tookMs: z.number(),
});
export type SearchResponse = z.infer<typeof searchResponseSchema>;

/** Typed payloads per card kind, so the UI is not guessing at `Record<string, unknown>`. */
export type CustomerHitPayload = {
  organization: string | null;
  addressLine: string | null;
  city: string | null;
  number: string | null;
  locale: 'fr' | 'en';
};

export type JobHitPayload = {
  customerId: string;
  customerName: string | null;
  addressLine: string | null;
  city: string | null;
  stage: Record<string, string> | null;
  stageColor: string | null;
  stageKind: string | null;
  scheduledStart: string | null;
  number: string;
};

export type InvoiceHitPayload = {
  customerId: string;
  customerName: string | null;
  status: string;
  total: string;
  balance: string;
  number: string;
  jobId: string | null;
  dueOn: string | null;
};

export type QuoteHitPayload = {
  customerId: string;
  customerName: string | null;
  status: string;
  total: string;
  number: string;
  jobId: string | null;
};

export type CallHitPayload = {
  customerId: string | null;
  customerName: string | null;
  direction: 'inbound' | 'outbound';
  status: string;
  durationSeconds: number;
  hasRecording: boolean;
  hasTranscript: boolean;
};

/**
 * The order results are shown in when grouped. Customers first because a person's name is
 * the most common thing anyone types, then the work, then the money.
 */
export const SEARCH_KIND_ORDER: readonly SearchKind[] = ['customer', 'job', 'quote', 'invoice', 'call', 'message'];

/** Where a hit navigates to. Kept here so the portal and field app cannot disagree. */
export function hitPath(hit: Pick<SearchHit, 'kind' | 'id' | 'payload'>): string {
  switch (hit.kind) {
    case 'customer':
      return `/customers/${hit.id}`;
    case 'job':
      return `/jobs/${hit.id}`;
    case 'quote':
      return `/quotes/${hit.id}`;
    case 'invoice':
      return `/invoices/${hit.id}`;
    case 'call': {
      // A call has no page of its own; it is shown on the customer it belongs to.
      const customerId = (hit.payload as CallHitPayload).customerId;
      return customerId ? `/customers/${customerId}?tab=calls&call=${hit.id}` : `/calls?call=${hit.id}`;
    }
    case 'message':
      return `/inbox?message=${hit.id}`;
  }
}
